/**
 * useTowAnimation — animates an aircraft to its tow destination and shows the sweep path.
 *
 * When a tow step is selected:
 * 1. Show the sweep path in the 3D scene via SweepPathRenderer
 * 2. Animate the aircraft from its current position to toPosition + toRotation over ~1.5s
 * 3. Persist the new position in the DB via updateAllocationPosition
 * 4. Call onComplete when done
 */

import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateAllocation } from '@/lib/api/allocations'
import type { SweepPathRenderer } from '../services/SweepPathRenderer'
import type { TowStep } from '../data/towingPlan'
import type * as THREE from 'three'

const ANIMATION_DURATION_MS = 1600

interface UseTowAnimationOptions {
  sweepPathRenderer: SweepPathRenderer | null
  scene: THREE.Scene | null
  /** Called when the step animation + DB write are done */
  onComplete: (stepId: string) => void
}

export function useTowAnimation({ sweepPathRenderer, scene: _scene, onComplete }: UseTowAnimationOptions) {
  const queryClient = useQueryClient()
  const rafRef = useRef<number | null>(null)
  const activeStepRef = useRef<TowStep | null>(null)

  const cancelAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    sweepPathRenderer?.hide()
    activeStepRef.current = null
  }, [sweepPathRenderer])

  const animateStep = useCallback(async (step: TowStep) => {
    cancelAnimation()
    activeStepRef.current = step

    // Show sweep path immediately
    sweepPathRenderer?.show(step)

    // Brief pause to let the user see the path before the aircraft starts moving
    await delay(600)

    if (activeStepRef.current !== step) return // cancelled

    // Animate the aircraft object in the 3D scene
    // We do this by directly finding the model in the scene and tweening its position/rotation.
    // This is the "Wizard of Oz" demo trick — the DB update happens at the end.
    const startTime = performance.now()
    const tick = () => {
      const elapsed = performance.now() - startTime
      const t = Math.min(elapsed / ANIMATION_DURATION_MS, 1)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        // Done — persist new position to DB
        rafRef.current = null
        sweepPathRenderer?.hide()
        activeStepRef.current = null

        persistPosition(step, queryClient).then(() => {
          onComplete(step.id)
        })
      }
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [cancelAnimation, sweepPathRenderer, queryClient, onComplete])

  // Cleanup on unmount
  useEffect(() => {
    return () => cancelAnimation()
  }, [cancelAnimation])

  return { animateStep, cancelAnimation }
}

async function persistPosition(step: TowStep, queryClient: ReturnType<typeof useQueryClient>) {
  try {
    const posType = step.type === 'park'
      ? (step.stepNumber === 3 ? 'hangar' : 'ramp')
      : 'hangar'

    await updateAllocation(step.allocationId, {
      position_x: step.toPosition[0],
      position_y: step.toPosition[1],
      position_z: step.toPosition[2],
      rotation: step.toRotation,
      position_type: posType as 'hangar' | 'ramp',
    })
    queryClient.invalidateQueries({ queryKey: ['map-allocations'] })
  } catch {
    // ignore — demo continues
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
